/**
 * Live Terminal Keyboard Shortcuts & Exit Dictionary Migrations.
 *
 * Dispatches terminal keyboard events with auto-repeat suppression and
 * input element guards. Also migrates legacy single-key exit configs to composite keys.
 */

export interface KeyboardEventLike {
  key: string;
  repeat?: boolean;
  target?: {
    tagName?: string;
  } | null;
}

export type KeyboardAction =
  | { type: "ignore" }
  | { type: "blur-input" }
  | { type: "dismiss-modal" }
  | { type: "order"; optionType: "CE" | "PE"; side: "BUY" | "SELL"; source: "key" }
  | { type: "square-off-all" }
  | { type: "cancel-all-orders" };

export const HOTKEY_ORDERS: Record<
  string,
  { optionType: "CE" | "PE"; side: "BUY" | "SELL" }
> = {
  ArrowUp: { optionType: "CE", side: "BUY" },
  ArrowLeft: { optionType: "CE", side: "SELL" },
  ArrowDown: { optionType: "PE", side: "BUY" },
  ArrowRight: { optionType: "PE", side: "SELL" },
};

/**
 * Resolves keyboard shortcut intent while filtering out auto-repeats and text input events.
 */
export function resolveKeyboardAction(e: KeyboardEventLike): KeyboardAction {
  if (e.repeat) return { type: "ignore" };

  const tag = (e.target?.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "SELECT") {
    if (e.key === "Escape") return { type: "blur-input" };
    return { type: "ignore" };
  }

  if (e.key === "Escape") {
    return { type: "dismiss-modal" };
  }

  const orderIntent = HOTKEY_ORDERS[e.key];
  if (orderIntent) {
    return {
      type: "order",
      optionType: orderIntent.optionType,
      side: orderIntent.side,
      source: "key",
    };
  }

  if (e.key === "F6") return { type: "square-off-all" };
  if (e.key === "F7") return { type: "cancel-all-orders" };

  return { type: "ignore" };
}

/**
 * Losslessly migrates armed exit dictionaries from legacy tsym keys to tsym|prd composite keys.
 */
export function migrateExitsKeyMap(
  rawExits: Record<string, any> | null | undefined,
  pkeyFn: (tsym: string, prd?: string) => string,
): Record<string, any> {
  const out: Record<string, any> = {};
  if (!rawExits || typeof rawExits !== "object") return out;

  for (const [k, ex] of Object.entries(rawExits)) {
    if (!ex) continue;
    const targetKey = k.includes("|") ? k : pkeyFn(k, ex.prd);
    out[targetKey] = ex;
  }

  return out;
}
